import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";
import type {
  Contact,
  ContactInput,
  ContactLogMethod,
  ContactPatch,
  ContactScope,
  ContactSignalMatch,
  ContactsAdapter,
  ContactsPage,
  ContactSource,
  ContactTemperature,
  ImportOptions,
  ImportPreview,
  ImportRow,
  ImportRowStatus,
} from "../types";
import { buildUpdateBody, resolveTemperature, textOrNull, type LiveContactRow } from "./mapping";

/**
 * Live contacts adapter (W3b) - bound to Codex's deployed v08 contact functions.
 *
 *   list_contacts        { scope: "own" | "all", limit, offset }  → roster with
 *                          organization_name, signal_match_count, matched_headlines,
 *                          temperature (+temperature_source), event_source, linkedin,
 *                          date_met, last_contact_date, contact_count, reminder_date,
 *                          reminder_time, reminder_reason, and owner {} (in "all")
 *   create_contact       { display_name, title, company, email, phone, notes,
 *                          linkedin, event_source, date_met, temperature,
 *                          reminder_date }
 *   update_contact       { contact_id, ...only the fields being changed }
 *   log_contact          { contact_id, method: "email" | "phone" | "in_person" }
 *   delete_contact       { contact_id }
 *   export_contacts_csv  { scope } → raw CSV text
 *   import_contacts_csv  { mode, csv, source_group, filters } → { plan | inserted_count }
 *
 * Every read and every mutation goes through supabase.functions.invoke(...); there
 * is no PostgREST table write and no supabase.rpc, so the client-write scanner
 * stays green with zero suppressions. The recipient (token) tier is a separate
 * adapter; this one runs on the authenticated internal session.
 *
 * Updates are partial: only the keys present in the patch are sent, so each
 * action writes what it owns without clobbering the rest (matches the Edge
 * Function's `if (body.key !== undefined)` convention - null clears, omit keeps).
 */

/* ============================================================
   Edge Function invocation (mirrors the signals live adapter)
   ============================================================ */

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS
  );
  if (error) throw new Error(`${name}: ${await messageFor(error)}`);
  return data as T;
}

async function invokeText(name: string, body: Record<string, unknown>): Promise<string> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS
  );
  if (error) throw new Error(`${name}: ${await messageFor(error)}`);
  if (typeof data === "string") return data;
  if (data instanceof Blob) return await data.text();
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
  return typeof data === "object" ? JSON.stringify(data) : String(data);
}

async function messageFor(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const parsed = (await ctx.json()) as { error?: { message?: string } };
      if (parsed?.error?.message) return parsed.error.message;
    } catch {
      /* non-JSON error body - fall through */
    }
  }
  return error instanceof Error ? error.message : String(error);
}

/* ============================================================
   Live response shapes (as returned by the v08 functions)
   ============================================================ */

interface LiveListEnvelope {
  data: { contacts: LiveContactRow[]; count: number; scope: string };
}
interface LiveContactEnvelope {
  data: { contact: LiveContactRow };
}

/** import_contacts_csv plan row (preview + commit).
 * Shapes differ by bucket: `to_create` carries the parsed fields flat;
 * `rejected_rows` nest the original CSV cells under `raw`; `duplicates` return
 * only `dedup_key` + `existing_contact_id` + `reason` (no source fields). */
interface LivePlanRow {
  row_number?: number;
  display_name?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  company_name?: string | null;
  notes?: string | null;
  linkedin?: string | null;
  event_source?: string | null;
  date_met?: string | null;
  dedup_key?: string | null;
  existing_contact_id?: string | null;
  reason?: string | null;
  message?: string | null;
  errors?: string[] | null;
  raw?: Record<string, string> | null;
}
interface LiveImportEnvelope {
  data: {
    mode: string;
    inserted_count?: number;
    plan: {
      total_rows: number;
      processed_rows: number;
      to_create: LivePlanRow[];
      duplicates: LivePlanRow[];
      rejected_rows: LivePlanRow[];
      counts: {
        total_rows: number;
        processed_rows: number;
        to_create: number;
        duplicates: number;
        rejected: number;
      };
    };
  };
}

/* ============================================================
   Row → Contact mapping
   ============================================================ */

/**
 * `fallback` carries values the caller just submitted, for a response whose
 * projection does not echo them (create_contact returns the identity columns
 * only). Each one is used ONLY when the row omits the field, so a server value
 * always wins and the fallback can never invent something the user did not
 * enter. Reads (list) and updates pass no fallback.
 */
function mapContact(
  row: LiveContactRow,
  viewerId: string | null,
  fallback?: {
    company?: string;
    temperature?: ContactTemperature;
    linkedin?: string | null;
    eventSource?: string | null;
    dateMet?: string | null;
    followUpDueAt?: string | null;
  }
): Contact {
  const headlines = row.matched_headlines ?? [];
  const matchCount = row.signal_match_count ?? headlines.length;
  const company = row.organization_name ?? row.company_name ?? fallback?.company ?? "";
  const matchedSignals: ContactSignalMatch[] = headlines.map((h) => ({
    signalId: h.signal_id,
    company,
    headline: h.headline,
    score: h.scf_score,
    tier: h.tier,
    sourceUrl: h.source_url ?? null,
  }));
  // Source tag: the origin marker wins (an uploaded contact stays "uploaded"
  // even after a signal match lands); otherwise a match makes it signal-sourced,
  // else manual. Binds the origin marker Codex shipped in d74ffc1.
  const source: ContactSource =
    row.contact_origin === "uploaded" ? "uploaded" : matchCount > 0 ? "signal" : "manual";
  const ownerId = row.owner_user_id ?? "";
  const ownerName =
    viewerId && ownerId === viewerId
      ? "You"
      : row.owner?.display_name || row.owner?.email || (viewerId ? "Teammate" : "You");
  const { temperature, temperatureSource } = resolveTemperature(row, fallback?.temperature);
  return {
    id: row.id,
    name: row.display_name ?? "",
    title: row.title ?? "",
    company,
    email: row.email ?? "",
    phone: row.phone ?? null,
    temperature,
    temperatureSource,
    activeEngagement: row.active_engagement ?? false,
    source,
    sourceDetail: row.event_source ?? fallback?.eventSource ?? null,
    linkedin: row.linkedin ?? row.linkedin_url ?? fallback?.linkedin ?? null,
    dateMet: row.date_met ?? fallback?.dateMet ?? null,
    matchedSignals,
    followUpDueAt:
      row.reminder_date ?? row.next_follow_up_due_at ?? fallback?.followUpDueAt ?? null,
    reminderTime: row.reminder_time ?? null,
    reminderReason: row.reminder_reason ?? null,
    notes: row.notes ?? "",
    lastTouchAt: row.last_contact_date ?? null,
    contactCount: typeof row.contact_count === "number" ? row.contact_count : 0,
    ownerId,
    ownerName,
  };
}

function scopeParam(scope: ContactScope): "own" | "all" {
  return scope === "all" ? "all" : "own";
}

/** import_contacts_csv controls: the source group to tag the batch with and the
 *  row-level filters. `source_group` is the documented deployed key; `batch_label`
 *  is sent as a compatibility alias (older sources keyed the group there). */
function importControls(options: ImportOptions): Record<string, unknown> {
  const group = textOrNull(options.sourceGroup);
  return {
    ...(options.ownerUserId ? { owner_user_id: options.ownerUserId } : {}),
    source_group: group,
    batch_label: group,
    filters: {
      seniority: options.filters.seniority,
      functions: options.filters.functions,
      exclude_keywords: options.filters.excludeKeywords,
      exclude_row_numbers: options.filters.excludeRowNumbers,
    },
  };
}

/** Read a field from a rejected row's `raw` CSV cells (case-insensitive header). */
function rawCell(raw: Record<string, string> | null | undefined, aliases: string[]): string {
  if (!raw) return "";
  for (const key of Object.keys(raw)) {
    if (aliases.includes(key.toLowerCase().trim())) return raw[key] ?? "";
  }
  return "";
}

const REASON_LABEL: Record<string, string> = {
  existing_contact: "Already in contacts",
  duplicate_in_file: "Duplicate row in file",
  missing_name: "Missing name",
  invalid_email: "Invalid email",
  missing_company: "Missing company",
  filtered_out: "Excluded by title filters",
  row_excluded: "Excluded from this upload",
  import_cap_exceeded: "Past the import limit",
};

function reasonMessage(row: LivePlanRow, status: ImportRowStatus): string {
  if (status === "valid") return "";
  const key = (row.reason || row.message || "").toString();
  if (key && REASON_LABEL[key]) return REASON_LABEL[key];
  if (Array.isArray(row.errors) && row.errors.length) return row.errors.join("; ");
  if (key) return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  return status === "duplicate" ? "Already in contacts" : "Rejected row";
}

/**
 * Map an import plan row into the UI's ImportRow (the server is the classifier).
 * Fields come from the flat keys (to_create), the nested `raw` cells (rejected),
 * or the dedup_key's email (duplicates return no source fields).
 */
function planRow(row: LivePlanRow, status: ImportRowStatus, index: number): ImportRow {
  const raw = row.raw;
  let email = row.email || rawCell(raw, ["email", "e-mail"]);
  if (!email && typeof row.dedup_key === "string" && row.dedup_key.startsWith("email:")) {
    email = row.dedup_key.slice("email:".length);
  }
  const input: ContactInput = {
    name: row.display_name || rawCell(raw, ["name", "full name", "contact"]),
    title: row.title || rawCell(raw, ["title", "role", "job title"]),
    company:
      row.company_name || rawCell(raw, ["company", "account", "organization", "organisation"]),
    email,
    phone: row.phone || rawCell(raw, ["phone", "mobile", "tel"]) || null,
    temperature: "warm",
    followUpDueAt: null,
    notes: row.notes || rawCell(raw, ["notes", "note", "comment"]),
    linkedin: row.linkedin || rawCell(raw, ["linkedin", "linkedin url", "linkedin_url"]) || null,
    eventSource:
      row.event_source || rawCell(raw, ["event", "source", "event source", "event_source"]) || null,
    dateMet: row.date_met || rawCell(raw, ["date met", "date", "date_met"]) || null,
  };
  return { line: row.row_number ?? index + 1, input, status, message: reasonMessage(row, status) };
}

function triggerCsvDownload(csv: string, filename: string): string {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  if (typeof document !== "undefined") {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return url;
}

/* ============================================================
   Adapter
   ============================================================ */

export class LiveContactsAdapter implements ContactsAdapter {
  /**
   * Fetch a single page of the roster and pass through the endpoint's exact
   * `count`. Resilient paging (page size, one-at-a-time loading, retry, and
   * progressive assembly) is orchestrated by the contacts hook; this method is
   * a thin per-page map. The endpoint accepts limit (1..500) and offset and
   * reports the true total in `count`.
   */
  async listContactsPage(
    scope: ContactScope,
    viewerId: string,
    opts: { limit: number; offset: number }
  ): Promise<ContactsPage> {
    const res = await invoke<LiveListEnvelope>("list_contacts", {
      scope: scopeParam(scope),
      limit: opts.limit,
      offset: opts.offset,
    });
    const contacts = (res.data?.contacts ?? []).map((row) => mapContact(row, viewerId));
    return { contacts, count: res.data?.count ?? contacts.length };
  }

  async addContact(input: ContactInput, viewerId: string): Promise<Contact> {
    // The structured values the create form already collects. The server payload
    // contract accepts every one of them, so send exactly what was entered - one
    // object, so the request and the cache fallback below cannot drift apart.
    const structured = {
      linkedin: textOrNull(input.linkedin),
      event_source: textOrNull(input.eventSource),
      date_met: textOrNull(input.dateMet),
      temperature: input.temperature,
      reminder_date: textOrNull(input.followUpDueAt),
    };
    const res = await invoke<LiveContactEnvelope>("create_contact", {
      display_name: input.name,
      title: textOrNull(input.title),
      company: textOrNull(input.company),
      email: textOrNull(input.email),
      phone: input.phone,
      notes: textOrNull(input.notes),
      ...structured,
    });
    // create_contact echoes the identity columns only, so without these the row
    // this add puts in the cache would read back as if nothing had been entered.
    // Update needs no equivalent: it applies its own ContactPatch to the cache.
    return mapContact(res.data.contact, viewerId, {
      company: input.company,
      temperature: structured.temperature,
      linkedin: structured.linkedin,
      eventSource: structured.event_source,
      dateMet: structured.date_met,
      followUpDueAt: structured.reminder_date,
    });
  }

  async updateContact(id: string, patch: ContactPatch): Promise<Contact> {
    const res = await invoke<LiveContactEnvelope>("update_contact", buildUpdateBody(id, patch));
    return mapContact(res.data.contact, null);
  }

  async logContact(id: string, method: ContactLogMethod): Promise<void> {
    await invoke("log_contact", { contact_id: id, method });
  }

  async deleteContact(id: string): Promise<void> {
    await invoke("delete_contact", { contact_id: id });
  }

  async exportCsv(scope: ContactScope, _viewerId: string): Promise<{ url: string }> {
    const sc = scopeParam(scope);
    const csv = await invokeText("export_contacts_csv", { scope: sc });
    return { url: triggerCsvDownload(csv, `contacts-${sc}.csv`) };
  }

  async previewCsvImport(
    file: File,
    _viewerId: string,
    options: ImportOptions
  ): Promise<ImportPreview> {
    const text = await file.text();
    const res = await invoke<LiveImportEnvelope>("import_contacts_csv", {
      mode: "preview",
      csv: text,
      ...importControls(options),
    });
    const plan = res.data.plan;
    const rows: ImportRow[] = [
      ...plan.to_create.map((r, i) => planRow(r, "valid", i)),
      ...plan.duplicates.map((r, i) => planRow(r, "duplicate", i)),
      ...plan.rejected_rows.map((r, i) =>
        planRow(
          r,
          r.reason === "filtered_out" || r.reason === "row_excluded" ? "excluded" : "error",
          i
        )
      ),
    ].sort((a, b) => a.line - b.line);
    return {
      filename: file.name,
      sourceText: text,
      rows,
      summary: {
        total: rows.length,
        valid: rows.filter((row) => row.status === "valid").length,
        duplicate: rows.filter((row) => row.status === "duplicate").length,
        excluded: rows.filter((row) => row.status === "excluded").length,
        error: rows.filter((row) => row.status === "error").length,
      },
      options,
    };
  }

  async confirmCsvImport(preview: ImportPreview, _viewerId: string): Promise<{ imported: number }> {
    // Commit re-sends the original CSV (and the same controls) with mode:"commit";
    // the server re-parses and re-dedups authoritatively, inserting the to_create set.
    const res = await invoke<LiveImportEnvelope>("import_contacts_csv", {
      mode: "commit",
      csv: preview.sourceText,
      ...importControls(preview.options),
    });
    return { imported: res.data.inserted_count ?? 0 };
  }
}
